import InvoiceAdjustmentDetails from '~/components/shared/InvoiceAdjustmentDetails';
import InvoiceReasons from '~/components/shared/InvoiceReasons';
/* global globalThis */
import trace from '~/lib/trace';
import React, {useState, useEffect, useCallback} from 'react';
import {View, ScrollView} from 'react-native';
import {Divider} from 'react-native-paper';
import moment from 'moment';
import isEqual from 'lodash/isEqual';
import isFinite from 'lodash/isFinite';
import pick from 'lodash/pick';
import pickBy from 'lodash/pickBy';
import LogRocket from 'logrocket';
import sy from '~/styles';
import {
  Text,
  Pressable,
  Link,
  FormTextInput,
  FormNumberInput,
  Calendar,
  MONTHS_3_PAST_6_FUTURE,
  MONTHS_1_PAST_6_FUTURE,
  TimeSlots,
  Select,
  Dialog,
  DealerPersona,
  LessorPersona,
  LessorPhone,
} from '~/components/controls';
import {Grouping} from '~/components/controls/3';
import {
  Row as GroupingRow,
  EditDialog as GroupingRowEdit,
} from '~/components/controls/3/Grouping';
import {
  RentalClassActionsheet,
  RentalCarActionsheet,
  DeliveryLocationActionsheet,
} from '~/components/shared/Actionsheets';
import {InvoiceUploadDialog} from '~/components/shared/Dialogs';
import Format from '~/lib/format';
import {
  rob as robApi,
  service_request as serviceRequestApi,
  fleet as fleetApi,
} from '~/api/private';
import {
  REPLACEMENT_REASON,
  REPLACEMENT_REASON_TITLE,
  FLEET_REPLACEMENT_REASON,
  FLEET_REPLACEMENT_REASON_TITLE,
  ROB_REPLACEMENT_RENTAL_CHARGE_CODE,
  ROB_REPLACEMENT_PICKUP_CHARGE_CODE,
  ROB_REPLACEMENT_DELIVERY_CHARGE_CODE,
  ROB_REPLACEMENT_KM_SURCHARGE_CODE,
  ROB_REPLACEMENT_FREE_KM_CODE,
  ROB_REPLACEMENT_STAGE,
  ROB_REPLACEMENT_STAGE_TITLE,
} from '~/types/replacement';
import {
  isLessorStatus,
  isArchived,
  isRobTaskApproved,
  isRobTaskCancelled,
  isNotInvoiced,
  ROB_NOT_INVOICED_STATUS_LABELS,
  ROB_STATUSES,
} from '~/types/rob';
import {STATUS_CODES, STATUS_DESCRIPTIONS} from '~/types/statuses';
import {REQUEST_TYPES} from '~/types/request';
import {
  setAppIsLoading,
  setAppDoneLoading,
  serviceRequestLastFetch,
} from '~/actions';
import {useDispatch, useSelector} from '~/lib/hooks';
import withDimensions from '~/components/hoc/with-dimensions';
import EventIcon from '~/images/md-icons/event/materialicons/24px.svg';
import CarRentalIcon from '~/images/md-icons/car_rental/materialicons/24px.svg';
import AssignmentIcon from '~/images/md-icons/assignment/materialicons/24px.svg';
import DateRangeIcon from '~/images/md-icons/date_range/materialicons/24px.svg';
import AddRoadIcon from '~/images/md-icons/add_road/materialicons/24px.svg';
import SyncAltIcon from '~/images/md-icons/sync_alt/materialicons/24px.svg';
import LocalGasStationIcon from '~/images/md-icons/local_gas_station/materialicons/24px.svg';
import GppBadIcon from '~/images/md-icons/gpp_bad/materialicons/24px.svg';
import ReceiptIcon from '~/images/md-icons/receipt/materialicons/24px.svg';
import BlockIcon from '~/images/md-icons/block/materialicons/24px.svg';
import DescriptionIcon from '~/images/md-icons/description/materialicons/24px.svg';
import BusinessIcon from '~/images/md-icons/business/materialicons/24px.svg';
import RefreshIcon from '~/images/md-icons/refresh/materialicons/24px.svg';
import WarningLargeIcon from '~/images/md-icons/warning/materialicons/24px.svg';
import FileOpenIcon from '~/images/md-icons/file_open/materialicons/24px.svg';
import AssignmentTurnedInIcon from '~/images/md-icons/assignment_turned_in/materialicons/24px.svg';
import CarTagIcon from '~/images/md-icons/car_tag/materialsymbolsfilled/24px.svg';
import {INVOICE_DISCOUNT_LABELS, INVOICE_DISCOUNT_ORDER} from '../constants';
import ServiceRequestContext from '../context';
import {pickDeep} from '../helper';
import PdfViewerLink from './PdfViewerLink';
import RentalClassText from './RentalClassText';
import FuelText from './FuelText';
import LineItem from './LineItem';
import Footer from './Footer';
const resetReplacementPricing = ({
  setRentalCharge,
  setKmSurcharge,
  setFreeKilometrage,
}) => {
  setRentalCharge(null);
  setKmSurcharge(null);
  setFreeKilometrage(null);
};
const shouldResetFromCarChange = (prevState, value) =>
  !!(
    value &&
    (prevState?.is_estate !== value?.is_estate ||
      prevState?.gearbox !== value?.gearbox)
  );
const shouldResetFromSimpleChange = (prevState, value) =>
  !!(value && prevState !== value);
const getReplacementChanges = ({
  replacement,
  start_date,
  start_time,
  durationInDays,
  reason,
  location,
  fuel,
  car,
  rental_class,
  daysCharged,
  extensionInDays,
  extensionComment,
  rentalCharge,
  kmSurcharge,
  freeKilometrage,
  kilometrage,
  costs,
}) => {
  if (!replacement) {
    return {};
  }
  const datetime = new Date(start_date);
  const time = new Date(start_time);
  datetime.setHours(time.getHours());
  datetime.setMinutes(time.getMinutes());
  return {
    type: replacement.type,
    sla: replacement.sla,
    datetime: datetime.toISOString(),
    duration_in_days: durationInDays,
    reason,
    location,
    car: car
      ? {
          fuel,
          ...car,
        }
      : {
          fuel,
        },
    rental_class,
    days_charged: daysCharged,
    extension_in_days: extensionInDays,
    extension_comment: extensionComment,
    charge_per_day: rentalCharge,
    surcharge: kmSurcharge,
    free_kilometrage:
      freeKilometrage >= 0 ? Format.round(freeKilometrage, 0) : null,
    kilometrage,
    costs,
  };
};

// The API and the form do not always format the same instant identically
// (e.g. `10:00:00Z` vs `10:00:00.000Z`), and the form keeps an empty location
// as `null` where the API omits it, so normalise both before comparing.
const normalizeReplacementChanges = ({datetime, location, ...value}) => ({
  ...value,
  ...(datetime && {
    datetime: moment(datetime).valueOf(),
  }),
  ...(location && {
    location,
  }),
});
const hasReplacementChanges = ({replacement, changes}) => {
  const existing = pickDeep({}, pick(replacement, Object.keys(changes)));
  return !isEqual(
    normalizeReplacementChanges(changes),
    normalizeReplacementChanges(existing),
  );
};

// Once a stage has been approved it may only be proposed again after the
// assignment has been changed.
const REPLACEMENT_APPROVED_STATUSES = new Set([
  STATUS_CODES.ProvisionallyApproved,
  STATUS_CODES.ReplacementReservationApproved,
  STATUS_CODES.ReplacementCheckoutApproved,
  STATUS_CODES.ReplacementExtensionApproved,
  STATUS_CODES.Approved,
]);
export const isReplacementStageApproved = ({fleet_request, replacement}) =>
  REPLACEMENT_APPROVED_STATUSES.has((fleet_request ?? replacement)?.status);
const useReplacementPricingState = ({replacement, service_request}) => {
  const [reason, setReason] = useState(
    replacement?.reason ?? REPLACEMENT_REASON.REPAIRS,
  );
  const [startDate, setStartDate] = useState(
    replacement?.datetime || service_request?.date,
  );
  const [startTime, setStartTime] = useState(
    replacement?.datetime || service_request?.datetime,
  );
  const [durationInDays, setDurationInDays] = useState(
    replacement?.duration_in_days || 1,
  );
  const [location, setLocation] = useState(replacement?.location ?? null);
  const [rentalCharge, setRentalCharge] = useState(replacement?.charge_per_day);
  const [kmSurcharge, setKmSurcharge] = useState(replacement?.surcharge);
  const [freeKilometrage, setFreeKilometrage] = useState(
    replacement?.free_kilometrage,
  );
  const [daysCharged, setDaysCharged] = useState(
    replacement?.days_charged ?? 1,
  );
  const [kilometrage, setKilometrage] = useState(replacement?.kilometrage);
  const [extensionInDays, setExtensionInDays] = useState(
    replacement?.extension_in_days,
  );
  const [extensionComment, setExtensionComment] = useState(
    replacement?.extension_comment,
  );
  const [costs, setCosts] = useState(replacement?.costs ?? {});
  const [carState, setCarState] = useState(
    replacement?.car?.license ? replacement?.car : null,
  );
  const [fuelState, setFuelState] = useState(replacement?.car?.fuel);
  const [rentalClassState, setRentalClassState] = useState(
    replacement?.rental_class,
  );
  const setCar = (value) => {
    setCarState((prevState) => {
      if (shouldResetFromCarChange(prevState, value)) {
        resetReplacementPricing({
          setRentalCharge,
          setKmSurcharge,
          setFreeKilometrage,
        });
      }
      return value;
    });
  };
  const setFuel = (value) => {
    setFuelState((prevState) => {
      if (shouldResetFromSimpleChange(prevState, value)) {
        resetReplacementPricing({
          setRentalCharge,
          setKmSurcharge,
          setFreeKilometrage,
        });
      }
      return value ?? prevState;
    });
  };
  const setRentalClass = (value) => {
    setRentalClassState((prevState) => {
      if (shouldResetFromSimpleChange(prevState, value)) {
        resetReplacementPricing({
          setRentalCharge,
          setKmSurcharge,
          setFreeKilometrage,
        });
      }
      return value ?? prevState;
    });
  };
  const setFuelIfEmpty = (value) => {
    setFuelState((prevState) => prevState ?? value);
  };
  const setRentalClassIfEmpty = (value) => {
    setRentalClassState((prevState) => prevState ?? value);
  };
  return {
    reason,
    setReason,
    start_date: startDate,
    setStartDate,
    start_time: startTime,
    setStartTime,
    durationInDays,
    setDurationInDays,
    location,
    setLocation,
    car: carState,
    setCar,
    fuel: fuelState,
    setFuel,
    setFuelIfEmpty,
    rental_class: rentalClassState,
    setRentalClass,
    setRentalClassIfEmpty,
    rentalCharge,
    setRentalCharge,
    kmSurcharge,
    setKmSurcharge,
    freeKilometrage,
    setFreeKilometrage,
    daysCharged,
    setDaysCharged,
    kilometrage,
    setKilometrage,
    extensionInDays,
    setExtensionInDays,
    extensionComment,
    setExtensionComment,
    costs,
    setCosts,
  };
};
const useReplacementUiState = () => {
  const [invoice, setInvoice] = useState(null);
  const [invoiceStatus, setInvoiceStatus] = useState(null);
  const [editInvoiceNumber, setEditInvoiceNumber] = useState(null);
  const [showEditCar, setShowEditCar] = useState(false);
  const [showEditRentalClass, setShowEditRentalClass] = useState(false);
  const [showNoInvoiceDialog, setShowNoInvoiceDialog] = useState(false);
  const [showNoInvoiceInfoDialog, setShowNoInvoiceInfoDialog] = useState(false);
  const [showInvoicePdfUploadDialog, setShowInvoicePdfUploadDialog] =
    useState(false);
  const [showDeliveryLocationActionsheet, setShowDeliveryLocationActionsheet] =
    useState(false);
  const [editDateTime, setEditDateTime] = useState(null);
  const [editDays, setEditDays] = useState(null);
  return {
    invoice,
    setInvoice,
    invoiceStatus,
    setInvoiceStatus,
    edit_invoiceNumber: editInvoiceNumber,
    setEditInvoiceNumber,
    showEditCar,
    setShowEditCar,
    showEditRentalClass,
    setShowEditRentalClass,
    showNoInvoiceDialog,
    setShowNoInvoiceDialog,
    showNoInvoiceInfoDialog,
    setShowNoInvoiceInfoDialog,
    showInvoicePdfUploadDialog,
    setShowInvoicePdfUploadDialog,
    showDeliveryLocationActionsheet,
    setShowDeliveryLocationActionsheet,
    editDateTime,
    setEditDateTime,
    editDays,
    setEditDays,
  };
};
const syncReplacementRefresh = async ({
  replacement,
  forceRefresh,
  lastFetch,
  dispatch,
  onRefresh,
}) => {
  const last_fetch_date = moment(
    lastFetch[replacement.request_id] ?? new Date(0),
  );
  const update_date = moment(
    forceRefresh[replacement.request_id]?.timestamp ?? new Date(0),
  );
  if (!update_date.isAfter(last_fetch_date, 'seconds')) {
    return;
  }
  dispatch(serviceRequestLastFetch(replacement.request_id));
  if (
    isLessorStatus(
      forceRefresh[replacement.request_id]?.status ??
        forceRefresh[replacement.request_id]?.rob_status,
    ) &&
    !isLessorStatus(replacement.status)
  ) {
    // Do a reload to make sure we have loaded the latest data
    if (globalThis.window !== undefined) {
      globalThis.location?.reload?.();
    }
    return;
  }
  await onRefresh();
};
const runWithReplacementLoading = async ({dispatch, task}) => {
  dispatch(setAppIsLoading());
  try {
    return await task();
  } catch (e) {
    console.error(e);
  } finally {
    dispatch(setAppDoneLoading());
  }
};
const getReplacementAssignmentStatusText = ({
  fleet_request,
  rob,
  replacement,
  stage,
}) => {
  if (isNotInvoiced((fleet_request ?? rob)?.invoicing?.status)) {
    return (
      <>
        &nbsp;&middot;&nbsp;
        {STATUS_DESCRIPTIONS[STATUS_CODES.NoInvoice]}
      </>
    );
  }
  if (!replacement?.status) {
    return <></>;
  }
  return (
    <>
      &nbsp;&middot;&nbsp;
      {isLessorStatus(replacement?.status)
        ? ROB_REPLACEMENT_STAGE_TITLE[stage]
        : STATUS_DESCRIPTIONS[replacement?.status]}
    </>
  );
};
const getReplacementAssignmentTotalText = ({
  stage,
  rentalCharge,
  kmSurcharge,
  total_amount,
}) => {
  if (stage === ROB_REPLACEMENT_STAGE.reservation) {
    return Format.price(0);
  }
  return Format.price(total_amount);
};
const getReplacementRentalLinePrice = ({
  extension_comment_missing,
  stage,
  daysCharged,
  rentalCharge,
}) => {
  if (extension_comment_missing) {
    return null;
  }
  if (stage !== ROB_REPLACEMENT_STAGE.reservation && daysCharged > 0) {
    return daysCharged * rentalCharge;
  }
  return <></>;
};
const isReplacementFinished = (status) =>
  [STATUS_CODES.Done, STATUS_CODES.DoneNoInvoice].includes(status);
const isReplacementTabDisabled = ({fleet_request, rob, replacement}) => {
  if (isLessorStatus(fleet_request?.status ?? rob?.rob_status)) {
    return true;
  }
  if (
    replacement &&
    (fleet_request || rob) &&
    ([
      STATUS_CODES.Cancelled,
      STATUS_CODES.Invoiced,
      STATUS_CODES.Archived,
    ].includes(replacement.status) ||
      (fleet_request ?? rob).invoicing?.status === 'Invoiced' ||
      isNotInvoiced((fleet_request ?? rob).invoicing?.status))
  ) {
    return true;
  }
  return false;
};
const resolveReplacementStage = ({
  rentalCharge,
  kmSurcharge,
  freeKilometrage,
  car,
  kilometrage,
}) => {
  let stage = ROB_REPLACEMENT_STAGE.reservation;
  if (
    isFinite(rentalCharge) &&
    isFinite(kmSurcharge) &&
    isFinite(freeKilometrage)
  ) {
    if (car?.license && isFinite(kilometrage)) {
      stage = ROB_REPLACEMENT_STAGE.checkin;
    } else if (car?.license) {
      stage = ROB_REPLACEMENT_STAGE.checkout;
    }
  }
  return stage;
};
const computeReplacementTotalAmount = ({
  fleet_request,
  rob,
  rentalCharge,
  daysCharged,
  surcharge,
  costs,
}) => {
  let total_amount = 0;
  if (!isRobTaskCancelled((fleet_request ?? rob)?.task_status)) {
    if (isFinite(rentalCharge)) {
      total_amount += daysCharged * rentalCharge;
    }
    if (surcharge) {
      total_amount += surcharge;
    }
    total_amount += Object.keys(costs)
      .filter((key) => !key.endsWith('_comment'))
      .filter((key) => isFinite(costs[key]))
      .reduce((acc, key) => acc + costs[key], 0);
  }
  return total_amount;
};
const renderReplacementLessorGrouping = ({
  service_request,
  robContract,
  isFleet,
  contract_rental_class,
  rental_classes,
  setRobContract,
}) => (
  <Grouping
    icon={
      <LessorPersona
        lessor={service_request?.lessor}
        fallback={
          <BusinessIcon
            fill={robContract?.isActive === false ? '#972727' : '#4A4A49'}
          />
        }
        size={24}
      />
    }
    header={service_request?.lessor}>
    {isFleet ? (
      <></>
    ) : (
      <GroupingRow title="Telefoonnummer">
        <LessorPhone lessor={robContract?.lessor} type="rental" />
      </GroupingRow>
    )}
    <GroupingRow title="Leaseauto">
      <Text>{service_request.car_name}</Text>
    </GroupingRow>
    <GroupingRow title="Huurklasse contract">
      {contract_rental_class ? (
        <RentalClassText
          rental_class={contract_rental_class}
          rental_classes={rental_classes}
        />
      ) : (
        <Text>Niet opgegeven</Text>
      )}
    </GroupingRow>
    <GroupingRow title="Brengkosten">
      <Text>
        {robContract?.permissions.replacement_vehicle.costs?.delivery ? (
          <>Toegestaan</>
        ) : (
          <>Niet toegestaan</>
        )}
      </Text>
    </GroupingRow>
    <GroupingRow title="Haalkosten">
      <Text>
        {robContract?.permissions.replacement_vehicle.costs?.pickup ? (
          <>Toegestaan</>
        ) : (
          <>Niet toegestaan</>
        )}
      </Text>
    </GroupingRow>
    <GroupingRow title="Aftankkosten">
      <Text>
        {robContract?.permissions.replacement_vehicle.costs?.fuel_service ? (
          <>Toegestaan</>
        ) : (
          <>Niet toegestaan</>
        )}
      </Text>
    </GroupingRow>

    {isFleet ? (
      <></>
    ) : (
      <GroupingRow>
        <View style={[sy['flex-row'], sy['justify-between']]}>
          {robContract?.isActive ? (
            <Pressable
              style={[sy['flex-row']]}
              onPress={async () => {
                const {result: rob_contract} = await robApi.update_contract(
                  service_request.license,
                  service_request.id,
                );
                setRobContract(rob_contract);
              }}>
              <View style={[sy['pr-8']]}>
                <RefreshIcon fill="#231fda" />
              </View>
              <View>
                <Link>Gegevens vernieuwen</Link>
                <Text style={[sy.smallRegular, sy['text-lightgray']]}>
                  Laatst bijgewerkt&nbsp;
                  {moment(robContract?.timestamp).format('D MMMM YYYY H:mm')}
                </Text>
              </View>
            </Pressable>
          ) : (
            <View style={sy['flex-row']}>
              <View style={[sy['pr-8']]}>
                <WarningLargeIcon fill="#972727" />
              </View>
              <View>
                <Text>Contract inactief</Text>
                <Text style={[sy.smallRegular, sy['text-lightgray']]}>
                  Laatst bijgewerkt&nbsp;
                  {moment(robContract?.timestamp).format('D MMMM YYYY H:mm')}
                </Text>
              </View>
            </View>
          )}
        </View>
      </GroupingRow>
    )}
  </Grouping>
);
const renderReplacementCarGrouping = ({
  car,
  is_disabled,
  setShowEditCar,
  rental_classes,
  contract_rental_class,
}) => (
  <Grouping
    icon={<CarRentalIcon fill="#4A4A49" />}
    header={
      car ? (
        <>
          {car.license}&nbsp;&middot;&nbsp;{car.name}
        </>
      ) : (
        'Auto inzet'
      )
    }>
    <GroupingRow title="Kenteken">
      <Link disabled={is_disabled('')} onPress={() => setShowEditCar(true)}>
        {car?.license ?? 'Keuze maken'}
      </Link>
    </GroupingRow>
    {car?.license && (
      <>
        <GroupingRow title="Auto">
          <Text>{car?.name}</Text>
        </GroupingRow>
        <GroupingRow
          title="Huurklasse inzet"
          style={[sy['gap-4']]}
          contentStyle={[sy['flex-1']]}
          disabled={is_disabled('')}>
          <RentalClassText
            style={[
              sy['text-right'],
              {
                display: 'block',
              },
            ]}
            rental_class={car?.rental_class}
            rental_classes={rental_classes}
            contract_rental_class={contract_rental_class}
          />
        </GroupingRow>
        <GroupingRow title="Brandstofsoort">
          <FuelText fuel={car?.fuel} />
        </GroupingRow>
        <GroupingRow title="Transmissie">
          <Text>
            {car?.gearbox === 'automatic' ? 'Automaat' : 'Handgeschakeld'}
          </Text>
        </GroupingRow>
        <GroupingRow title="Carrosserie">
          <Text>{car?.is_estate ? 'Stationwagen' : 'Overig'}</Text>
        </GroupingRow>
      </>
    )}
  </Grouping>
);
const renderReplacementPricingGrouping = ({
  rental_class,
  sla,
  rentalCharge,
  kmSurcharge,
  is_disabled,
  setShowEditRentalClass,
  rental_classes,
  contract_rental_class,
  car,
  setRentalCharge,
  setKmSurcharge,
  setFreeKilometrage,
  freeKilometrage,
}) => (
  <Grouping
    icon={<CarTagIcon fill="#4A4A49" />}
    header={
      <>
        {rental_class}
        {sla && isFinite(rentalCharge) && isFinite(kmSurcharge) && (
          <>
            &nbsp;&middot;&nbsp;
            <>{Format.price(rentalCharge)}</>
            &nbsp;(<>{Format.price(kmSurcharge)}</>)
          </>
        )}
      </>
    }>
    <GroupingRow
      title="Huurklasse"
      style={[sy['gap-4']]}
      contentStyle={[sy['flex-1']]}
      disabled={is_disabled()}>
      <Link
        disabled={is_disabled()}
        onPress={() => setShowEditRentalClass(true)}>
        <RentalClassText
          style={[
            sy['text-right'],
            {
              display: 'block',
            },
          ]}
          rental_class={rental_class}
          rental_classes={rental_classes}
          contract_rental_class={contract_rental_class}
          rental_car_class={car?.rental_class}
        />
      </Link>
    </GroupingRow>
    <GroupingRow title="Dagtarief" disabled={is_disabled()}>
      <Text>
        {isFinite(rentalCharge) ? Format.price(rentalCharge) : <>Opgeven</>}
      </Text>
      <GroupingRowEdit
        title="Dagtarief"
        initialValue={rentalCharge}
        dismissable
        cancelButton
        onCancel={({initialValue}) => setRentalCharge(initialValue)}>
        <View style={sy['p-4']}>
          <FormNumberInput
            value={rentalCharge}
            digits={2}
            onChangeNumber={setRentalCharge}
          />
          <Text
            style={[
              sy.smallRegular,
              {
                paddingLeft: 12,
              },
              sy['text-lightgray'],
            ]}>
            SLA:&nbsp;
            {sla?.[ROB_REPLACEMENT_RENTAL_CHARGE_CODE]?.value ? (
              <>
                {Format.price(sla?.[ROB_REPLACEMENT_RENTAL_CHARGE_CODE]?.value)}
              </>
            ) : (
              <>Geen</>
            )}
          </Text>

          {sla?.[ROB_REPLACEMENT_RENTAL_CHARGE_CODE]?.surcharges.map(
            (surcharge) => (
              <Text
                key={surcharge.item}
                style={[
                  sy.smallRegular,
                  {
                    paddingLeft: 12,
                  },
                  sy['text-lightgray'],
                ]}>
                {surcharge.item}:&nbsp;{Format.price(surcharge.amount)}
              </Text>
            ),
          )}
        </View>
      </GroupingRowEdit>
    </GroupingRow>
    <GroupingRow title="Meerkilometertarief" disabled={is_disabled('')}>
      <Text>
        {isFinite(kmSurcharge) ? Format.price(kmSurcharge) : <>Opgeven</>}
      </Text>
      <GroupingRowEdit
        title="Meerkilometertarief"
        initialValue={kmSurcharge}
        dismissable
        cancelButton
        onCancel={({initialValue}) => setKmSurcharge(initialValue)}>
        <View style={sy['p-4']}>
          <FormNumberInput
            value={kmSurcharge}
            digits={2}
            onChangeNumber={setKmSurcharge}
          />
          <Text
            style={[
              sy.smallRegular,
              {
                paddingLeft: 12,
              },
              sy['text-lightgray'],
            ]}>
            SLA:&nbsp;
            {sla?.[ROB_REPLACEMENT_KM_SURCHARGE_CODE]?.amount ? (
              Format.price(sla?.[ROB_REPLACEMENT_KM_SURCHARGE_CODE]?.amount)
            ) : (
              <>Geen</>
            )}
          </Text>
        </View>
      </GroupingRowEdit>
    </GroupingRow>
    <GroupingRow title="Vrije kilometers per dag" disabled={is_disabled('')}>
      <Text>
        {isFinite(freeKilometrage) ? (
          Format.number(freeKilometrage)
        ) : (
          <>Opgeven</>
        )}
      </Text>
      <GroupingRowEdit
        title="Vrije kilometers per dag"
        initialValue={freeKilometrage}
        dismissable
        cancelButton
        onCancel={({initialValue}) => setFreeKilometrage(initialValue)}>
        <View style={sy['p-4']}>
          <FormNumberInput
            value={freeKilometrage}
            digits={0}
            onChangeNumber={setFreeKilometrage}
          />
          <Text
            style={[
              sy.smallRegular,
              {
                paddingLeft: 12,
              },
              sy['text-lightgray'],
            ]}>
            SLA:&nbsp;
            {sla?.[ROB_REPLACEMENT_FREE_KM_CODE]?.amount ? (
              <>
                {Format.number(sla?.[ROB_REPLACEMENT_FREE_KM_CODE]?.amount, 0)}
                &nbsp;km&nbsp;vrij
              </>
            ) : (
              <>Geen</>
            )}
          </Text>
        </View>
      </GroupingRowEdit>
    </GroupingRow>
  </Grouping>
);
const renderReplacementInvoiceNumberRow = ({
  fleet_request,
  rob,
  replacement,
  invoice,
  service_request,
  setEditInvoiceNumber,
  edit_invoiceNumber,
  onUpdateRequest,
}) => {
  if (isNotInvoiced((fleet_request ?? rob)?.invoicing?.status)) {
    return (
      <GroupingRow title="Reden">
        <Text>
          {
            ROB_NOT_INVOICED_STATUS_LABELS[
              (fleet_request ?? rob)?.invoicing?.status
            ]
          }
        </Text>
      </GroupingRow>
    );
  }
  if (
    isArchived((fleet_request ?? rob)?.status) ||
    (fleet_request ?? rob)?.invoicing?.status === 'Invoiced'
  ) {
    return null;
  }
  return (
    <GroupingRow
      linkStyle={[
        {
          color:
            !replacement?.invoice_number && !invoice?.bahnkick_can_invoice
              ? '#FF6C00'
              : null,
        },
      ]}
      title={
        <View>
          <View style={[sy['flex-row'], sy['items-center']]}>
            <Text>Nummer</Text>
          </View>
          {!replacement?.invoice_number &&
            !invoice?.bahnkick_can_invoice &&
            invoice?.pdf_upload && (
              <View>
                <Text style={[sy.smallRegular, sy['text-lightgray']]}>
                  Let op: facturatie aan <>{service_request.lessor}</>
                </Text>
              </View>
            )}
        </View>
      }
      onEditClicked={() =>
        setEditInvoiceNumber({
          value: replacement?.invoice_number,
          valid: true,
        })
      }>
      <>
        {invoice?.bahnkick_can_invoice && !replacement.invoice_number ? (
          <Text style={sy.smallRegular}>(Wordt gegenereerd)</Text>
        ) : (
          replacement.invoice_number ?? <>Opgeven</>
        )}
      </>
      {invoice?.bahnkick_can_invoice ? null : (
        <GroupingRowEdit
          title="Factuur"
          dismissable
          cancelButton
          onValidate={async () => {
            if (edit_invoiceNumber.value?.length > 0) {
              const result = await onUpdateRequest(edit_invoiceNumber.value);
              if (result) {
                return true;
              }
              setEditInvoiceNumber((prevState) => ({
                ...prevState,
                valid: false,
              }));
              return false;
            }
          }}>
          <View style={[sy['p-4']]}>
            <FormTextInput
              label="Nummer"
              value={edit_invoiceNumber?.value}
              hasError={edit_invoiceNumber?.valid === false}
              errorMessage="Factuurnummer opslaan is mislukt. Controleer het factuurnummer en probeer het opnieuw."
              onEndEditing={(value) => {
                setEditInvoiceNumber((prevState) => ({
                  ...prevState,
                  value,
                  valid: true,
                }));
              }}
            />
          </View>
        </GroupingRowEdit>
      )}
    </GroupingRow>
  );
};
const renderReplacementInvoiceActions = ({
  fleet_request,
  rob,
  setShowNoInvoiceDialog,
  onRequest,
}) => {
  if (isArchived((fleet_request ?? rob)?.status)) {
    return null;
  }
  return (
    <>
      {(fleet_request ?? rob)?.invoicing?.status === 'ReadyForInvoicing' && (
        <View style={[sy['p-4'], sy['flex-row']]}>
          <BlockIcon />
          <Link
            style={[sy['pl-8']]}
            onPress={() => setShowNoInvoiceDialog(true)}>
            Niet factureren
          </Link>
        </View>
      )}
      {isNotInvoiced((fleet_request ?? rob)?.invoicing?.status) && (
        <View style={[sy['p-4'], sy['flex-row']]}>
          <ReceiptIcon />
          <Link style={[sy['pl-8']]} onPress={onRequest}>
            Wel factureren
          </Link>
        </View>
      )}
    </>
  );
};
const renderReplacementInvoiceSection = ({
  replacement,
  fleet_request,
  rob,
  invoice,
  total_amount,
  service_request,
  setEditInvoiceNumber,
  edit_invoiceNumber,
  onUpdateRequest,
  dimensions,
  setShowNoInvoiceDialog,
  onRequest,
}) => {
  if (
    !(
      replacement?.status === STATUS_CODES.Done ||
      (fleet_request ?? rob)?.invoicing?.status === STATUS_CODES.Invoiced
    )
  ) {
    return null;
  }
  return (
    <>
      <Divider style={sy['bg-gray88']} />
      <View
        style={[
          sy['flex-row'],
          sy['items-center'],
          sy['justify-between'],
          sy['p-4'],
        ]}>
        <View style={[sy['flex-row'], sy['flex-1']]}>
          <View style={[sy['pr-8']]}>
            <ReceiptIcon
              fill={
                !isNotInvoiced((fleet_request ?? rob)?.invoicing?.status) &&
                !replacement?.invoice_number &&
                !invoice?.bahnkick_can_invoice
                  ? '#FF6C00'
                  : '#4A4A49'
              }
            />
          </View>
          <Text style={sy.mediumPlus}>
            {isNotInvoiced((fleet_request ?? rob)?.invoicing?.status) ? (
              <>Geen factuur</>
            ) : (
              <>
                Factuur
                {replacement.invoice_number ? (
                  <>&nbsp;&middot;&nbsp;{replacement.invoice_number}</>
                ) : (
                  <></>
                )}
              </>
            )}
          </Text>
        </View>
        {isNotInvoiced((fleet_request ?? rob)?.invoicing?.status) ? (
          <></>
        ) : (
          <Text style={sy.mediumPlus}>
            {Format.price(invoice?.amount ?? total_amount)}
          </Text>
        )}
      </View>
      <Divider style={sy['bg-gray88']} />
      {renderReplacementInvoiceNumberRow({
        fleet_request,
        rob,
        replacement,
        invoice,
        service_request,
        setEditInvoiceNumber,
        edit_invoiceNumber,
        onUpdateRequest,
      })}
      {!isNotInvoiced((fleet_request ?? rob)?.invoicing?.status) &&
        invoice?.components && (
          <>
            <GroupingRow
              title={
                <View>
                  <Text>Opdracht</Text>
                  <Text style={[sy.smallRegular, sy['text-lightgray']]}>
                    Exclusief BTW
                  </Text>
                </View>
              }>
              <Text style={sy.smallRegular}>
                {Format.price(invoice.amount_approved)}
              </Text>
            </GroupingRow>
            {Format.round(
              invoice.amount_approved -
                (invoice?.amount_approved_discount ?? 0) -
                (invoice?.amount ?? total_amount),
            ) > 0 && (
              <GroupingRow
                title={
                  <View>
                    <Text>Inhouding</Text>
                  </View>
                }>
                <Text style={sy.smallRegular}>
                  {Format.price(
                    invoice.amount_approved -
                      (invoice?.amount_approved_discount ?? 0) -
                      (invoice?.amount ?? total_amount),
                  )}
                </Text>
                <InvoiceAdjustmentDetails
                  title="Inhouding"
                  dimensions={dimensions}>
                  {Object.keys(invoice.components)
                    .filter((type) => invoice.components[type].amount > 0)
                    .sort(
                      (a, b) =>
                        INVOICE_DISCOUNT_ORDER.indexOf(a) -
                        INVOICE_DISCOUNT_ORDER.indexOf(b),
                    )
                    .map((type) => {
                      const item = invoice.components[type];
                      return (
                        <GroupingRow
                          key={type}
                          title={
                            <View>
                              <Text>
                                {INVOICE_DISCOUNT_LABELS[item.type] ?? (
                                  <>Korting</>
                                )}
                              </Text>
                              <Text
                                style={[sy.smallRegular, sy['text-lightgray']]}>
                                <>
                                  {['DEALER_FEE_AMOUNT'].includes(item.type) ? (
                                    Format.price(item.amount)
                                  ) : (
                                    <>
                                      {Format.number(
                                        item.discount * 100,
                                        1,
                                        false,
                                        {
                                          minimumFractionDigits: 0,
                                        },
                                      )}
                                      %
                                    </>
                                  )}
                                </>
                                {!INVOICE_DISCOUNT_LABELS[item.type] &&
                                  item.lessor && (
                                    <>&nbsp;&middot;&nbsp;{item.lessor}</>
                                  )}
                                {item.subject && (
                                  <>&nbsp;&middot;&nbsp;{item.subject}</>
                                )}
                              </Text>
                            </View>
                          }>
                          <Text style={sy.smallRegular}>
                            {Format.price(item.amount)}
                          </Text>
                        </GroupingRow>
                      );
                    })}
                </InvoiceAdjustmentDetails>
              </GroupingRow>
            )}
            {invoice?.amount_approved_discount > 0 && (
              <GroupingRow
                title={
                  <View>
                    <Text>Korting</Text>
                  </View>
                }>
                <Text style={sy.smallRegular}>
                  {Format.price(invoice?.amount_approved_discount)}
                </Text>
                <InvoiceAdjustmentDetails
                  title="Korting"
                  dimensions={dimensions}>
                  {Object.keys(invoice.components)
                    .filter(
                      (type) =>
                        type === 'SLA_APPROVED_INVOICE_DISCOUNT' &&
                        invoice.components[type].amount > 0,
                    )
                    .sort(
                      (a, b) =>
                        INVOICE_DISCOUNT_ORDER.indexOf(a) -
                        INVOICE_DISCOUNT_ORDER.indexOf(b),
                    )
                    .map((type) => {
                      const item = invoice.components[type];
                      return (
                        <GroupingRow
                          key={type}
                          title={
                            <View>
                              <Text>
                                {INVOICE_DISCOUNT_LABELS[item.type] ?? (
                                  <>Korting</>
                                )}
                              </Text>
                              <Text
                                style={[sy.smallRegular, sy['text-lightgray']]}>
                                <>
                                  {Format.number(
                                    item.discount * 100,
                                    1,
                                    false,
                                    {
                                      minimumFractionDigits: 0,
                                    },
                                  )}
                                  %
                                </>
                                {item.lessor && (
                                  <>&nbsp;&middot;&nbsp;{item.lessor}</>
                                )}
                                {item.subject && (
                                  <>&nbsp;&middot;&nbsp;{item.subject}</>
                                )}
                              </Text>
                            </View>
                          }>
                          <Text style={sy.smallRegular}>
                            {Format.price(item.amount)}
                          </Text>
                        </GroupingRow>
                      );
                    })}
                </InvoiceAdjustmentDetails>
              </GroupingRow>
            )}
          </>
        )}
      <GroupingRow
        title={
          <View>
            <Text>Totaal</Text>
            <Text style={[sy.smallRegular, sy['text-lightgray']]}>
              Exclusief BTW
            </Text>
          </View>
        }>
        <Text style={sy.smallRegular}>
          {Format.price(invoice?.amount ?? total_amount)}
        </Text>
      </GroupingRow>

      {(invoice?.bahnkick_can_invoice || invoice?.pdf_available) &&
        replacement?.invoice_number && (
          <GroupingRow
            title={
              <PdfViewerLink
                invoice={invoice}
                request_id={replacement.request_id}
              />
            }>
            <FileOpenIcon />
          </GroupingRow>
        )}
      {renderReplacementInvoiceActions({
        fleet_request,
        rob,
        setShowNoInvoiceDialog,
        onRequest,
      })}
    </>
  );
};
const ReplacementRequestTab = withDimensions(
  ({dimensions, focused, onChangeTab}) => {
    const {
      request: service_request,
      replacement,
      rob,
      fleetRequest: fleet_request,
      is_fleet_car,
      onRefresh,
    } = React.useContext(ServiceRequestContext);
    const [robContract, setRobContract] = useState(null);
    const [rentalClasses, setRentalClasses] = useState(null);
    const {
      reason,
      setReason,
      start_date,
      setStartDate,
      start_time,
      setStartTime,
      durationInDays,
      setDurationInDays,
      location,
      setLocation,
      car,
      setCar,
      fuel,
      setFuel,
      setFuelIfEmpty,
      rental_class,
      setRentalClass,
      setRentalClassIfEmpty,
      rentalCharge,
      setRentalCharge,
      kmSurcharge,
      setKmSurcharge,
      freeKilometrage,
      setFreeKilometrage,
      daysCharged,
      setDaysCharged,
      kilometrage,
      setKilometrage,
      extensionInDays,
      setExtensionInDays,
      extensionComment,
      setExtensionComment,
      costs,
      setCosts,
    } = useReplacementPricingState({
      replacement,
      service_request,
    });
    const [sla, setSla] = useState(null);
    const {
      invoice,
      setInvoice,
      invoiceStatus,
      setInvoiceStatus,
      edit_invoiceNumber,
      setEditInvoiceNumber,
      showEditCar,
      setShowEditCar,
      showEditRentalClass,
      setShowEditRentalClass,
      showNoInvoiceDialog,
      setShowNoInvoiceDialog,
      showNoInvoiceInfoDialog,
      setShowNoInvoiceInfoDialog,
      showInvoicePdfUploadDialog,
      setShowInvoicePdfUploadDialog,
      showDeliveryLocationActionsheet,
      setShowDeliveryLocationActionsheet,
      editDateTime,
      setEditDateTime,
      editDays,
      setEditDays,
    } = useReplacementUiState();
    const dispatch = useDispatch();
    const forceRefresh = useSelector((state) => state.serviceRequestUpdate);
    const lastFetch = useSelector((state) => state.serviceRequestLastFetch);
    const isFleet = !!service_request.fleet_contract_id;
    const getChanges = useCallback(() => {
      return getReplacementChanges({
        replacement,
        start_date,
        start_time,
        durationInDays,
        reason,
        location,
        fuel,
        car,
        rental_class,
        daysCharged,
        extensionInDays,
        extensionComment,
        rentalCharge,
        kmSurcharge,
        freeKilometrage,
        kilometrage,
        costs,
      });
    }, [
      replacement,
      start_date,
      start_time,
      durationInDays,
      reason,
      location,
      fuel,
      car,
      rental_class,
      daysCharged,
      extensionInDays,
      extensionComment,
      rentalCharge,
      kmSurcharge,
      freeKilometrage,
      kilometrage,
      costs,
    ]);
    useEffect(() => {
      LogRocket.track(
        'Service Request Details',
        pickBy({
          request_id: service_request.id,
          license: service_request.license,
          status: service_request.status,
          rob_status: service_request.rob?.status,
        }),
      );
    }, [service_request]);
    useEffect(() => {
      if (focused) {
        onRefresh();
      }
    }, [focused, onRefresh]);
    useEffect(() => {
      const fetch = async () => {
        if (!replacement) {
          return;
        }
        await syncReplacementRefresh({
          replacement,
          forceRefresh,
          lastFetch,
          dispatch,
          onRefresh,
        });
      };
      fetch().catch(trace);
    }, [replacement, forceRefresh, lastFetch, dispatch, onRefresh]);
    useEffect(() => {
      const fetch = async () => {
        if (!service_request?.id) {
          return;
        }
        const {result: rentalClassesResult} = await robApi.rental_classes();
        setRentalClasses(rentalClassesResult);
        let contract;
        if (isFleet) {
          const {result} = await fleetApi.contract(
            service_request.fleet_contract_id,
          );
          contract = result.contract;
        } else {
          ({result: contract} = await robApi.contract(
            service_request.license,
            service_request.id,
          ));
        }
        setRobContract(contract);
        setFuelIfEmpty(
          contract?.leaseContract?.rentalVehicle.fuelType.toLowerCase(),
        );
        setRentalClassIfEmpty(
          contract?.leaseContract?.rentalVehicle.rentalClass ??
            rentalClassesResult[0].code,
        );
        if (
          replacement &&
          (fleet_request || rob) &&
          (replacement.status === STATUS_CODES.Done ||
            replacement.status === STATUS_CODES.Invoiced ||
            replacement.status === STATUS_CODES.Archived ||
            (fleet_request ?? rob).invoicing?.status === 'ReadyForInvoicing' ||
            (fleet_request ?? rob).invoicing?.status === 'Invoiced')
        ) {
          const {result: invoice} = await serviceRequestApi.invoice(
            replacement.request_id,
          );
          setInvoice(invoice);
        }
      };
      fetch().catch(trace);
    }, [service_request, replacement, rob]);
    useEffect(() => {
      if (replacement?.request_id && fuel && rental_class) {
        const fetch = async () => {
          const {result: sla} = await serviceRequestApi.sla_replacement_vehicle(
            replacement.request_id,
            fuel,
            rental_class,
            car?.is_estate,
            car?.gearbox,
          );
          setSla(sla);
          setRentalCharge(
            (prevState) =>
              prevState ?? sla[ROB_REPLACEMENT_RENTAL_CHARGE_CODE]?.amount,
          );
          setKmSurcharge(
            (prevState) =>
              prevState ?? sla[ROB_REPLACEMENT_KM_SURCHARGE_CODE]?.amount,
          );
          setFreeKilometrage(
            (prevState) =>
              prevState ?? sla[ROB_REPLACEMENT_FREE_KM_CODE]?.amount,
          );
        };
        fetch().catch(trace);
      }
    }, [service_request, replacement, fuel, rental_class, car]);
    useEffect(() => {
      if (!sla) {
        return;
      }
      if (location) {
        setCosts((prevState) => ({
          ...prevState,
          pickup:
            prevState.pickup ??
            (robContract?.permissions.replacement_vehicle.costs?.delivery
              ? sla[ROB_REPLACEMENT_PICKUP_CHARGE_CODE]?.amount
              : null),
          delivery:
            prevState.delivery ??
            (robContract?.permissions.replacement_vehicle.costs?.pickup
              ? sla[ROB_REPLACEMENT_DELIVERY_CHARGE_CODE]?.amount
              : null),
        }));
      } else {
        setCosts((prevState) => ({
          ...prevState,
          pickup: null,
          delivery: null,
        }));
      }
    }, [sla, location]);
    useEffect(() => {
      const fetch = async () => {
        const changes = pickDeep({}, getChanges());
        const existing = pickDeep({}, pick(replacement, Object.keys(changes)));
        if (isEqual(changes, existing)) {
          return;
        }
        await serviceRequestApi.patch(replacement.request_id, {
          replacement: changes,
        });
        const {success} = await serviceRequestApi.change_status(
          replacement.request_id,
          STATUS_CODES.Modified,
        );
        if (!success) {
          // The request did not accept the status change, so leave the ROB
          // task alone: reactivating it would revoke the approval and strand
          // the request without a way to invoice it.
          await onRefresh();
          return;
        }
        if (rob) {
          await robApi.activate_task(
            replacement.request_id,
            rob.rob_id,
            rob.task_id,
          );
        } else {
          await onRefresh();
        }
      };
      if (
        !isReplacementFinished(replacement?.status) &&
        (fleet_request?.status ?? rob?.rob_status) === ROB_STATUSES.Approved
      ) {
        fetch().catch(trace);
      }
    }, [
      getChanges,
      replacement,
      rob,
      fleet_request,
      start_date,
      start_time,
      durationInDays,
      reason,
      location,
      fuel,
      car,
      rental_class,
      daysCharged,
      extensionInDays,
      extensionComment,
      rentalCharge,
      kmSurcharge,
      freeKilometrage,
      kilometrage,
      costs,
    ]);
    const onRequest = async () => {
      const changes = getChanges();
      if (isFleet) {
        await fleetApi.request_replacement_vehicle(service_request.id, changes);
      } else {
        await robApi.request_replacement_vehicle(service_request.id, changes);
      }
      onRefresh();
    };
    const onFinish = async () => {
      await serviceRequestApi.change_status(
        replacement.request_id,
        STATUS_CODES.Done,
      );
      onRefresh();
    };
    const onUpdateRequest = async (invoice_number) => {
      const result = await serviceRequestApi.patch(replacement.request_id, {
        invoice_number,
      });
      if (result?.success) {
        onRefresh();
      }
      return result?.success;
    };
    const onInvoice = async () => {
      if (invoice?.pdf_upload && !invoice?.bahnkick_can_invoice) {
        setShowInvoicePdfUploadDialog(true);
      } else {
        await InvoiceServiceRequest();
      }
    };
    const InvoiceServiceRequest = async (pdf) => {
      return runWithReplacementLoading({
        dispatch,
        task: async () => {
          let result;
          if (isFleet) {
            result = await fleetApi.invoice(replacement.request_id, pdf);
          } else {
            result = await robApi.invoice(
              replacement.request_id,
              replacement.invoice_number,
            );
          }
          if (result?.success) {
            onRefresh();
            return null;
          }
          return result?.errors;
        },
      });
    };
    const onChangeInvoiceStatus = async () => {
      await runWithReplacementLoading({
        dispatch,
        task: async () => {
          if (replacement.invoice_number) {
            // Clear the entered invoice number
            await serviceRequestApi.patch(replacement.request_id, {
              datetime: replacement.datetime,
              invoice_number: null,
            });
          }
          const {success} = await robApi.change_invoice_status(
            replacement.request_id,
            invoiceStatus,
          );
          if (success) {
            setShowNoInvoiceDialog(false);
            setShowNoInvoiceInfoDialog(true);
            onRefresh();
          }
        },
      });
    };
    const is_disabled = (_key) =>
      isReplacementTabDisabled({
        fleet_request,
        rob,
        replacement,
      });
    const stage = resolveReplacementStage({
      rentalCharge,
      kmSurcharge,
      freeKilometrage,
      car,
      kilometrage,
    });
    const contract_rental_class =
      robContract?.leaseContract?.rentalVehicle.rentalClass;
    const total_duration_in_days = durationInDays + (extensionInDays ?? 0);
    const surcharge = isFinite(kilometrage)
      ? kmSurcharge *
        Math.max(kilometrage - freeKilometrage * total_duration_in_days, 0)
      : null;
    const total_amount = computeReplacementTotalAmount({
      fleet_request,
      rob,
      rentalCharge,
      daysCharged,
      surcharge,
      costs,
    });
    const extension_comment_missing = extensionInDays > 0 && !extensionComment;
    const stage_unchanged =
      isReplacementStageApproved({
        fleet_request,
        replacement,
      }) &&
      !hasReplacementChanges({
        replacement,
        changes: pickDeep({}, getChanges()),
      });
    return (
      <>
        <ScrollView
          style={[sy['bg-gray95']]}
          contentContainerStyle={[sy['p-2'], sy['gap-2'], sy['mb-14']]}>
          <Grouping
            icon={
              <DealerPersona
                dealer_id={service_request?.dealer_id}
                fallback={<EventIcon fill="#4A4A49" />}
                size={24}
              />
            }
            header={
              <>
                <Text style={[sy.mediumPlus, sy.capitalize]}>
                  {moment(start_date, 'YYYY-MM-DD').format('dddd')}
                </Text>
                &nbsp;{moment(start_date, 'YYYY-MM-DD').format('D MMMM')}
                &nbsp;&middot;&nbsp;{total_duration_in_days}
                &nbsp;dag
                {total_duration_in_days === 1 ? '' : 'en'}
              </>
            }
            options={{
              topMost: true,
            }}>
            <GroupingRow
              title="Vestiging"
              style={[sy['gap-4']]}
              contentStyle={[sy['flex-1']]}>
              <Text style={[sy['truncate'], sy['text-right']]}>
                {service_request.dealer_city}&nbsp;&middot;&nbsp;
                {service_request.dealer_short_name}
              </Text>
            </GroupingRow>
            <GroupingRow title="Startdatum" disabled={is_disabled('')}>
              <Text>
                {moment(start_date, 'YYYY-MM-DD').format('dddd D MMMM YYYY')}
              </Text>
              <GroupingRowEdit
                title="Startdatum wijzigen"
                dismissable
                cancelButton
                disableOKButton={!editDateTime}
                onClose={() => setEditDateTime(null)}
                onOK={() => setStartDate(editDateTime)}>
                <Calendar
                  months={MONTHS_3_PAST_6_FUTURE}
                  initialDate={start_date}
                  date={editDateTime ?? start_date}
                  onPress={setEditDateTime}
                />
              </GroupingRowEdit>
            </GroupingRow>
            <GroupingRow title="Starttijd" disabled={is_disabled('')}>
              <Text>{moment.utc(start_time).format('H:mm')}</Text>
              <GroupingRowEdit
                title="Starttijd wijzigen"
                dismissable
                cancelButton
                disableOKButton={!editDateTime}
                onClose={() => setEditDateTime(null)}
                onOK={() => setStartTime(editDateTime)}>
                <ScrollView
                  style={[
                    sy['pl-4'],
                    {
                      maxHeight: 400,
                    },
                  ]}>
                  <TimeSlots
                    begin_checkin={moment().startOf('day').hours(6)}
                    end_checkin={moment().startOf('day').hours(19)}
                    datetime={editDateTime ?? start_time}
                    onPress={setEditDateTime}
                  />
                </ScrollView>
              </GroupingRowEdit>
            </GroupingRow>
            <GroupingRow title="Einddatum" disabled={is_disabled('')}>
              <Text>
                {moment(start_date, 'YYYY-MM-DD')
                  .add(total_duration_in_days - 1, 'days')
                  .format('dddd D MMMM YYYY')}
              </Text>
              <GroupingRowEdit
                title="Einddatum wijzigen"
                dismissable
                cancelButton
                disableOKButton={!editDays}
                onClose={() => setEditDays(null)}
                onOK={() => {
                  setDaysCharged(editDays + (extensionInDays ?? 0));
                  setDurationInDays(editDays);
                }}>
                <Calendar
                  months={MONTHS_1_PAST_6_FUTURE}
                  initialDate={moment(start_date, 'YYYY-MM-DD').add(
                    total_duration_in_days - 1,
                    'days',
                  )}
                  date={moment(start_date, 'YYYY-MM-DD').add(
                    (editDays ?? total_duration_in_days) - 1,
                    'days',
                  )}
                  minDate={start_date}
                  onClose={() => setEditDays(null)}
                  onPress={(date) => {
                    const days = moment(
                      moment(date).add(1, 'days').startOf('day'),
                    ).diff(moment(start_date).startOf('day'), 'days');
                    setEditDays(days);
                  }}
                />
              </GroupingRowEdit>
            </GroupingRow>
            <GroupingRow title="Reden inzet" disabled={is_disabled('')}>
              <Text>
                {isFleet
                  ? FLEET_REPLACEMENT_REASON_TITLE[reason]
                  : REPLACEMENT_REASON_TITLE[reason]}
              </Text>
              <GroupingRowEdit
                title="Reden inzet"
                initialValue={reason}
                dismissable
                cancelButton
                onCancel={({initialValue}) => setReason(initialValue)}>
                <View style={sy['p-4']}>
                  <Select
                    label="Reden"
                    value={reason}
                    options={Object.values(
                      isFleet ? FLEET_REPLACEMENT_REASON : REPLACEMENT_REASON,
                    ).map((item) => ({
                      text: isFleet
                        ? FLEET_REPLACEMENT_REASON_TITLE[item]
                        : REPLACEMENT_REASON_TITLE[item],
                      value: item,
                    }))}
                    onChange={(option) => setReason(option.value)}
                  />
                </View>
              </GroupingRowEdit>
            </GroupingRow>
            {!isFleet && (
              <GroupingRow title="Locatie" disabled={is_disabled('')}>
                <Link
                  disabled={is_disabled('')}
                  onPress={() => setShowDeliveryLocationActionsheet(true)}>
                  {location?.name ?? 'Eigen locatie'}
                </Link>
              </GroupingRow>
            )}
          </Grouping>

          {renderReplacementLessorGrouping({
            service_request,
            robContract,
            isFleet,
            contract_rental_class,
            rental_classes: rentalClasses,
            setRobContract,
          })}

          {renderReplacementCarGrouping({
            car,
            is_disabled,
            setShowEditCar,
            rental_classes: rentalClasses,
            contract_rental_class,
          })}

          {renderReplacementPricingGrouping({
            rental_class,
            sla,
            rentalCharge,
            kmSurcharge,
            is_disabled,
            setShowEditRentalClass,
            rental_classes: rentalClasses,
            contract_rental_class,
            car,
            setRentalCharge,
            setKmSurcharge,
            setFreeKilometrage,
            freeKilometrage,
          })}

          <View style={[sy['bg-white'], sy['rounded-lg']]}>
            <View
              style={[
                sy['p-4'],
                sy['gap-4'],
                sy['flex-row'],
                sy['items-center'],
              ]}>
              <AssignmentIcon fill="#4A4A49" />
              <View
                style={[
                  sy['flex-row'],
                  sy['justify-between'],
                  sy['flex-1'],
                  sy['items-center'],
                ]}>
                <Text style={sy.mediumPlus}>
                  Opdracht
                  {getReplacementAssignmentStatusText({
                    fleet_request,
                    rob,
                    replacement,
                    stage,
                  })}
                </Text>
                <Text style={sy.mediumPlus}>
                  {getReplacementAssignmentTotalText({
                    stage,
                    rentalCharge,
                    kmSurcharge,
                    total_amount,
                  })}
                </Text>
              </View>
            </View>
            <Divider style={sy['bg-gray88']} />
            <LineItem
              topMost={true}
              icon={
                <DateRangeIcon
                  fill={extension_comment_missing ? '#FF6C00' : '#4A4A49'}
                />
              }
              title="Verhuur"
              disabled={is_disabled('')}
              cancelled={isRobTaskCancelled(
                (fleet_request ?? rob)?.task_status,
              )}
              price={getReplacementRentalLinePrice({
                extension_comment_missing,
                stage,
                daysCharged,
                rentalCharge,
              })}
              subtitleColor={extension_comment_missing ? '#FF6C00' : null}
              subtitle={
                <>
                  {daysCharged}&nbsp;dag{daysCharged === 1 ? '' : 'en'}
                </>
              }
              initialValues={{
                daysCharged,
                rentalCharge,
                durationInDays,
                extensionInDays,
                extensionComment,
              }}
              onValidate={({extensionInDays, extensionComment}) => {
                if (extensionInDays > 0 && !extensionComment) {
                  return {
                    isValid: false,
                    errors: {
                      extensionComment: true,
                    },
                  };
                }
              }}
              onChange={({
                daysCharged,
                rentalCharge,
                durationInDays,
                extensionInDays,
                extensionComment,
              }) => {
                setDaysCharged(daysCharged);
                setRentalCharge(rentalCharge);
                setDurationInDays(durationInDays);
                setExtensionInDays(extensionInDays);
                setExtensionComment(extensionComment);
              }}
              editDialog={({changes, setChanges, errors}) => (
                <>
                  <View style={[sy['p-4']]}>
                    <FormNumberInput
                      label="Aantal dagen factureren"
                      value={changes.daysCharged}
                      digits={0}
                      onChangeNumber={(daysCharged) =>
                        setChanges((prevState) => {
                          let extensionInDays = prevState.extensionInDays;
                          let durationInDays = prevState.durationInDays;
                          if (
                            isRobTaskApproved(
                              (fleet_request ?? rob)?.stages[
                                ROB_REPLACEMENT_STAGE.checkout
                              ]?.status,
                            )
                          ) {
                            extensionInDays = Math.max(
                              extensionInDays ?? 0,
                              daysCharged - durationInDays,
                              0,
                            );
                          } else {
                            durationInDays = Math.max(
                              durationInDays,
                              daysCharged,
                            );
                          }
                          return {
                            ...prevState,
                            daysCharged,
                            extensionInDays,
                            durationInDays,
                          };
                        })
                      }
                    />
                    <Text
                      style={[
                        sy.smallRegular,
                        {
                          paddingLeft: 12,
                        },
                        sy['text-lightgray'],
                      ]}>
                      {changes.durationInDays + (changes.extensionInDays ?? 0)}
                      &nbsp;dag
                      {changes.durationInDays +
                        (changes.extensionInDays ?? 0) ===
                      1
                        ? ''
                        : 'en'}
                      &nbsp;inzet
                      {changes.extensionInDays > 0 && (
                        <>&nbsp;(incl. verlenging)</>
                      )}
                    </Text>
                  </View>
                  {[
                    ROB_REPLACEMENT_STAGE.checkin,
                    ROB_REPLACEMENT_STAGE.checkout,
                  ].includes(stage) && (
                    <>
                      <View style={[sy['p-4'], sy['pt-0']]}>
                        <FormNumberInput
                          label="Dagtarief (euro)"
                          digits={2}
                          value={changes.rentalCharge}
                          onChangeNumber={(number) =>
                            setChanges((prevState) => ({
                              ...prevState,
                              rentalCharge: number,
                            }))
                          }
                        />
                        <Text
                          style={[
                            sy.smallRegular,
                            {
                              paddingLeft: 12,
                            },
                            sy['text-lightgray'],
                          ]}>
                          SLA:&nbsp;
                          {sla?.[ROB_REPLACEMENT_RENTAL_CHARGE_CODE]?.value ? (
                            Format.price(
                              sla?.[ROB_REPLACEMENT_RENTAL_CHARGE_CODE]?.value,
                            )
                          ) : (
                            <>Geen</>
                          )}
                        </Text>
                        {sla?.[
                          ROB_REPLACEMENT_RENTAL_CHARGE_CODE
                        ]?.surcharges.map((surcharge) => (
                          <Text
                            key={surcharge.item}
                            style={[
                              sy.smallRegular,
                              {
                                paddingLeft: 12,
                              },
                              sy['text-lightgray'],
                            ]}>
                            {surcharge.item}:&nbsp;
                            {Format.price(surcharge.amount)}
                          </Text>
                        ))}
                      </View>
                      <View style={[sy['p-4'], sy['pt-0']]}>
                        <FormNumberInput
                          label="Kosten (euro)"
                          value={changes.daysCharged * changes.rentalCharge}
                          onChangeNumber={(number) =>
                            setChanges((prevState) => ({
                              ...prevState,
                              rentalCharge: number / changes.daysCharged,
                            }))
                          }
                        />
                      </View>
                    </>
                  )}
                  {changes.extensionInDays > 0 && (
                    <View style={[sy['p-4'], sy['pt-0'], sy['gap-4']]}>
                      <FormNumberInput
                        label="Aantal dagen verlenging"
                        value={changes.extensionInDays}
                        digits={0}
                        onChangeNumber={(extensionInDays) =>
                          setExtensionInDays(extensionInDays)
                        }
                      />
                      <FormTextInput
                        label="Reden verlenging"
                        value={changes.extensionComment}
                        errorMessage="Dit veld is verplicht"
                        hasError={
                          (!changes.extensionCommentChanged &&
                            extension_comment_missing) ||
                          errors?.extensionComment
                        }
                        onChangeText={(text) =>
                          setChanges((prevState) => ({
                            ...prevState,
                            extensionComment: text,
                            extensionCommentChanged: true,
                          }))
                        }
                        inputProps={{
                          multiline: true,
                          numberOfLines: 8,
                        }}
                      />
                    </View>
                  )}
                </>
              )}
            />
            {[
              ROB_REPLACEMENT_STAGE.checkin,
              ROB_REPLACEMENT_STAGE.checkout,
            ].includes(stage) && (
              <LineItem
                icon={<AddRoadIcon fill="#4A4A49" />}
                title="Meerkilometers"
                disabled={is_disabled('')}
                cancelled={isRobTaskCancelled(
                  (fleet_request ?? rob)?.task_status,
                )}
                price={surcharge}
                subtitle={
                  <>
                    {Format.number(
                      Math.max(
                        kilometrage - freeKilometrage * total_duration_in_days,
                        0,
                      ),
                    )}
                    &nbsp;km
                  </>
                }
                initialValues={{
                  freeKilometrage,
                  kilometrage,
                }}
                onChange={({freeKilometrage, kilometrage}) => {
                  setFreeKilometrage(freeKilometrage);
                  setKilometrage(kilometrage);
                }}
                editDialog={({changes, setChanges}) => (
                  <>
                    <View style={[sy['p-4']]}>
                      <FormNumberInput
                        label="Verreden kilometers"
                        value={changes.kilometrage}
                        digits={0}
                        onChangeNumber={(number) =>
                          setChanges((prevState) => ({
                            ...prevState,
                            kilometrage: number,
                          }))
                        }
                      />
                    </View>
                    <View style={[sy['p-4'], sy['pt-0']]}>
                      <FormNumberInput
                        label="Vrije kilometers"
                        value={changes.freeKilometrage * total_duration_in_days}
                        digits={0}
                        onChangeNumber={(number) =>
                          setChanges((prevState) => ({
                            ...prevState,
                            freeKilometrage: Math.round(
                              number / total_duration_in_days,
                              0,
                            ),
                          }))
                        }
                      />
                      <Text
                        style={[
                          sy.smallRegular,
                          {
                            paddingLeft: 12,
                          },
                          sy['text-lightgray'],
                        ]}>
                        SLA:&nbsp;
                        {sla?.[ROB_REPLACEMENT_FREE_KM_CODE]?.amount ? (
                          <>
                            {Format.number(
                              sla?.[ROB_REPLACEMENT_FREE_KM_CODE]?.amount *
                                total_duration_in_days,
                              0,
                            )}
                            &nbsp;km&nbsp;vrij
                          </>
                        ) : (
                          <>Geen</>
                        )}
                      </Text>
                    </View>
                    <View style={[sy['p-4'], sy['pt-0']]}>
                      <FormNumberInput
                        label="Meerkilometers"
                        value={
                          isFinite(changes.kilometrage)
                            ? Math.max(
                                changes.kilometrage -
                                  changes.freeKilometrage *
                                    total_duration_in_days,
                                0,
                              )
                            : null
                        }
                        digits={0}
                        onChangeNumber={(number) =>
                          setChanges((prevState) => ({
                            ...prevState,
                            kilometrage: Math.max(
                              freeKilometrage * total_duration_in_days + number,
                              0,
                            ),
                          }))
                        }
                      />
                    </View>
                  </>
                )}
              />
            )}
            {location && (
              <LineItem
                icon={<SyncAltIcon fill="#4A4A49" />}
                title="Breng- en haalkosten"
                allowed={
                  robContract?.permissions.replacement_vehicle.costs
                    ?.delivery ||
                  robContract?.permissions.replacement_vehicle.costs?.pickup
                }
                disabled={is_disabled('')}
                cancelled={isRobTaskCancelled(
                  (fleet_request ?? rob)?.task_status,
                )}
                price={
                  isFinite(costs.delivery) || isFinite(costs.pickup)
                    ? (costs.delivery ?? 0) + (costs.pickup ?? 0)
                    : null
                }
                initialValues={costs}
                onChange={({delivery, pickup}) => {
                  setCosts((prevState) => ({
                    ...prevState,
                    delivery,
                    pickup,
                  }));
                }}
                editDialog={({changes, setChanges}) => (
                  <>
                    <View style={[sy['p-4']]}>
                      <FormNumberInput
                        label="Brengkosten (euro)"
                        digits={2}
                        disabled={
                          !robContract?.permissions.replacement_vehicle.costs
                            ?.delivery
                        }
                        value={changes.delivery}
                        onChangeNumber={(number) =>
                          setChanges((prevState) => ({
                            ...prevState,
                            delivery: number,
                          }))
                        }
                      />
                      <Text
                        style={[
                          sy.smallRegular,
                          {
                            paddingLeft: 12,
                          },
                          sy['text-lightgray'],
                        ]}>
                        SLA:&nbsp;
                        {sla?.[ROB_REPLACEMENT_DELIVERY_CHARGE_CODE]?.amount ? (
                          <>
                            {Format.price(
                              sla?.[ROB_REPLACEMENT_DELIVERY_CHARGE_CODE]
                                ?.amount,
                            )}
                          </>
                        ) : (
                          <>Geen</>
                        )}
                      </Text>
                    </View>
                    <View style={[sy['p-4'], sy['pt-0']]}>
                      <FormNumberInput
                        label="Haalkosten (euro)"
                        disabled={
                          !robContract?.permissions.replacement_vehicle.costs
                            ?.pickup
                        }
                        digits={2}
                        value={changes.pickup}
                        onChangeNumber={(number) =>
                          setChanges((prevState) => ({
                            ...prevState,
                            pickup: number,
                          }))
                        }
                      />
                      <Text
                        style={[
                          sy.smallRegular,
                          {
                            paddingLeft: 12,
                          },
                          sy['text-lightgray'],
                        ]}>
                        SLA:&nbsp;
                        {sla?.[ROB_REPLACEMENT_PICKUP_CHARGE_CODE]?.amount ? (
                          <>
                            {Format.price(
                              sla?.[ROB_REPLACEMENT_PICKUP_CHARGE_CODE]?.amount,
                            )}
                          </>
                        ) : (
                          <>Geen</>
                        )}
                      </Text>
                    </View>
                  </>
                )}
              />
            )}
            {[ROB_REPLACEMENT_STAGE.checkin].includes(stage) && (
              <>
                <LineItem
                  icon={<LocalGasStationIcon fill="#4A4A49" />}
                  title="Brandstof- en aftankkosten"
                  allowed={
                    robContract?.permissions.replacement_vehicle.costs?.fuel ||
                    robContract?.permissions.replacement_vehicle.costs
                      ?.fuel_service
                  }
                  disabled={is_disabled('')}
                  cancelled={isRobTaskCancelled(
                    (fleet_request ?? rob)?.task_status,
                  )}
                  price={
                    isFinite(costs.fuel) || isFinite(costs.fuel_service)
                      ? (costs.fuel ?? 0) + (costs.fuel_service ?? 0)
                      : null
                  }
                  initialValues={costs}
                  onChange={({fuel, fuel_service}) => {
                    setCosts((prevState) => ({
                      ...prevState,
                      fuel,
                      fuel_service,
                    }));
                  }}
                  editDialog={({changes, setChanges}) => (
                    <>
                      <View style={[sy['p-4']]}>
                        <FormNumberInput
                          label="Brandstofkosten (euro)"
                          digits={2}
                          disabled={
                            !robContract?.permissions.replacement_vehicle.costs
                              ?.fuel
                          }
                          value={changes.fuel}
                          onChangeNumber={(number) =>
                            setChanges((prevState) => ({
                              ...prevState,
                              fuel: number,
                            }))
                          }
                        />
                      </View>
                      <View style={[sy['p-4'], sy['pt-0']]}>
                        <FormNumberInput
                          label="Aftankkosten (euro)"
                          disabled={
                            !robContract?.permissions.replacement_vehicle.costs
                              ?.fuel_service
                          }
                          digits={2}
                          value={changes.fuel_service}
                          onChangeNumber={(number) =>
                            setChanges((prevState) => ({
                              ...prevState,
                              fuel_service: number,
                            }))
                          }
                        />
                      </View>
                    </>
                  )}
                />
                <LineItem
                  icon={<GppBadIcon fill="#4A4A49" />}
                  title="Eigen risico claim"
                  allowed={
                    robContract?.permissions.replacement_vehicle.costs?.claim
                  }
                  disabled={is_disabled('')}
                  cancelled={isRobTaskCancelled(
                    (fleet_request ?? rob)?.task_status,
                  )}
                  price={costs.claim}
                  initialValues={costs}
                  onValidate={({claim, claim_comment}) => {
                    const hasError = isFinite(claim) && !claim_comment;
                    return {
                      isValid: !hasError,
                      errors: {
                        claim_comment: hasError,
                      },
                    };
                  }}
                  onChange={({claim, claim_comment}) => {
                    setCosts((prevState) => ({
                      ...prevState,
                      claim,
                      claim_comment,
                    }));
                  }}
                  editDialog={({changes, setChanges, errors}) => (
                    <>
                      <View style={[sy['p-4']]}>
                        <FormNumberInput
                          label="Bedrag (euro)"
                          digits={2}
                          value={changes.claim}
                          onChangeNumber={(number) =>
                            setChanges((prevState) => ({
                              ...prevState,
                              claim: number,
                            }))
                          }
                        />
                      </View>
                      <View style={[sy['p-4'], sy['pt-0']]}>
                        <FormTextInput
                          label="Toelichting"
                          value={changes.claim_comment}
                          errorMessage="Dit veld is verplicht"
                          hasError={errors?.claim_comment}
                          onChangeText={(text) =>
                            setChanges((prevState) => ({
                              ...prevState,
                              claim_comment: text,
                            }))
                          }
                          inputProps={{
                            multiline: true,
                            numberOfLines: 8,
                          }}
                        />
                      </View>
                    </>
                  )}
                />
              </>
            )}
            <LineItem
              icon={<DescriptionIcon fill="#4A4A49" />}
              title="Overige kosten"
              allowed={robContract?.permissions.replacement_vehicle.costs?.misc}
              disabled={is_disabled('')}
              cancelled={isRobTaskCancelled(
                (fleet_request ?? rob)?.task_status,
              )}
              price={costs.misc}
              initialValues={costs}
              onValidate={({misc, misc_comment}) => {
                const hasError = isFinite(misc) && !misc_comment;
                return {
                  isValid: !hasError,
                  errors: {
                    misc_comment: hasError,
                  },
                };
              }}
              onChange={({misc, misc_comment}) => {
                setCosts((prevState) => ({
                  ...prevState,
                  misc,
                  misc_comment,
                }));
              }}
              editDialog={({changes, setChanges, errors}) => (
                <>
                  <View style={[sy['p-4']]}>
                    <FormNumberInput
                      label="Bedrag (euro)"
                      digits={2}
                      value={changes.misc}
                      onChangeNumber={(number) =>
                        setChanges((prevState) => ({
                          ...prevState,
                          misc: number,
                        }))
                      }
                    />
                  </View>
                  <View style={[sy['p-4'], sy['pt-0']]}>
                    <FormTextInput
                      label="Toelichting"
                      value={changes.misc_comment}
                      errorMessage="Dit veld is verplicht"
                      hasError={errors?.misc_comment}
                      onChangeText={(text) =>
                        setChanges((prevState) => ({
                          ...prevState,
                          misc_comment: text,
                        }))
                      }
                      inputProps={{
                        multiline: true,
                        numberOfLines: 8,
                      }}
                    />
                  </View>
                </>
              )}
            />

            {renderReplacementInvoiceSection({
              replacement,
              fleet_request,
              rob,
              invoice,
              total_amount,
              service_request,
              setEditInvoiceNumber,
              edit_invoiceNumber,
              onUpdateRequest,
              dimensions,
              setShowNoInvoiceDialog,
              onRequest,
            })}
          </View>
        </ScrollView>

        <Footer
          disabled={extension_comment_missing || is_disabled()}
          stageDisabled={stage_unchanged}
          is_fleet_car={is_fleet_car}
          replacement={replacement}
          status={replacement?.status}
          hasSLA={
            isFinite(rentalCharge) &&
            isFinite(kmSurcharge) &&
            isFinite(freeKilometrage)
          }
          car={car}
          kilometrage={kilometrage}
          invoiceNumber={replacement?.invoice_number}
          service_request={service_request}
          rob={rob}
          fleet_request={fleet_request}
          invoice={invoice}
          onRequest={onRequest}
          onFinish={onFinish}
          onInvoice={onInvoice}
          onRefresh={onRefresh}
          onChangeTab={onChangeTab}
        />
        <RentalClassActionsheet
          visible={showEditRentalClass}
          request_id={replacement?.request_id}
          rental_class={rental_class}
          contract_rental_class={contract_rental_class}
          rental_car_class={car?.rental_class}
          fuel={fuel}
          onChange={(value) => {
            setRentalClass(value);
            setShowEditRentalClass(false);
          }}
          onClose={() => setShowEditRentalClass(false)}
        />
        <RentalCarActionsheet
          visible={showEditCar}
          request_id={replacement?.request_id}
          is_fleet_car={
            service_request?.request_type === REQUEST_TYPES.FLEET_REQUEST
          }
          dealer_id={replacement?.dealer_id}
          license={car?.license}
          fuel={fuel}
          rental_class={rental_class}
          contract_rental_class={contract_rental_class}
          onChange={async (car) => {
            let rental_class = contract_rental_class;
            if (car?.rental_class && contract_rental_class) {
              const {result: sla} =
                await serviceRequestApi.sla_replacement_vehicle(
                  replacement.request_id,
                  car?.fuel,
                  car?.rental_class,
                  car?.is_estate,
                  car?.gearbox,
                );
              const rental_class_charge =
                sla?.[ROB_REPLACEMENT_RENTAL_CHARGE_CODE];
              const contract_rental_class_charge =
                sla?.contract?.[ROB_REPLACEMENT_RENTAL_CHARGE_CODE];
              if (
                isFinite(rental_class_charge?.amount) &&
                (!isFinite(contract_rental_class_charge?.amount) ||
                  rental_class_charge?.amount <
                    contract_rental_class_charge?.amount)
              ) {
                rental_class = car.rental_class;
              }
            }
            setCar(car);
            setFuel(car?.fuel);
            setRentalClass(rental_class);
          }}
          onClose={() => setShowEditCar(false)}
        />
        {showNoInvoiceInfoDialog && (
          <Dialog
            visible={true}
            title="Gereed zonder factuur"
            buttons={[
              {
                text: 'OK',
                textStyle: {
                  color: '#972727',
                },
                onPress: () => setShowNoInvoiceInfoDialog(false),
              },
            ]}>
            <View style={[sy['flex-row'], sy['py-4']]}>
              <View
                style={[
                  {
                    flexShrink: 0,
                  },
                  sy['pr-4'],
                ]}>
                <AssignmentTurnedInIcon
                  height="24px"
                  width="24px"
                  fill="#828282"
                />
              </View>
              <Text>
                Opdracht niet gefactureerd. Toch wel factureren? Dit is mogelijk
                tot aan archivering.
              </Text>
            </View>
          </Dialog>
        )}
        {showNoInvoiceDialog && (
          <Dialog
            visible={true}
            title="Reden geen factuur"
            buttons={[
              {
                text: 'Annuleren',
                onPress: () => setShowNoInvoiceDialog(false),
              },
              {
                text: 'Opslaan',
                textStyle: {
                  color: '#972727',
                },
                onPress: onChangeInvoiceStatus,
              },
            ]}
            options={{
              noPaddingContent: true,
            }}>
            <InvoiceReasons
              invoiceStatus={invoiceStatus}
              setInvoiceStatus={setInvoiceStatus}
            />
          </Dialog>
        )}

        {showInvoicePdfUploadDialog && (
          <InvoiceUploadDialog
            visible={true}
            lessor={service_request?.lessor}
            onDismiss={() => setShowInvoicePdfUploadDialog(false)}
            onOK={async (data) => {
              const errors = await InvoiceServiceRequest(data);
              if (errors) {
                throw errors;
              } else {
                setShowInvoicePdfUploadDialog(false);
              }
            }}
          />
        )}

        {showDeliveryLocationActionsheet && (
          <DeliveryLocationActionsheet
            dealer_id={replacement?.dealer_id}
            location={location}
            onDismiss={() => setShowDeliveryLocationActionsheet(false)}
            onChange={(location) => {
              if (location) {
                setLocation({
                  id: location.id,
                  name: location.name,
                  phone: location.phone_number,
                  address: {
                    zipCode: location.postal_code,
                    houseNumber: location.house_number,
                    street: location.street,
                    city: location.city,
                  },
                });
              } else {
                setLocation(null);
              }
              setShowDeliveryLocationActionsheet(false);
            }}
          />
        )}
      </>
    );
  },
);
export default ReplacementRequestTab;
